import React from 'react';
import { Link } from 'react-router-dom';
import { Skeleton } from '../ui/Skeleton';

// Full class strings so Tailwind can see them at build time.
const TONES = {
  black: { bar: 'bg-zinc-950', track: 'bg-zinc-200', icon: 'text-zinc-950', soft: 'bg-zinc-100 text-zinc-800' },
  red: { bar: 'bg-[#DD0000]', track: 'bg-[#DD0000]/15', icon: 'text-[#DD0000]', soft: 'bg-[#DD0000]/[0.07] text-[#B00000]' },
  gold: { bar: 'bg-[#FFCE00]', track: 'bg-[#FFCE00]/25', icon: 'text-[#B38F00]', soft: 'bg-[#FFF8D9] text-[#806700]' },
};

const actionClass =
  'inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-zinc-200/70 dark:border-white/10 bg-white/80 dark:bg-zinc-800/80 backdrop-blur-xs px-3 py-1.5 text-sm font-medium text-zinc-900 dark:text-zinc-100 transition hover:bg-zinc-100/80 dark:hover:bg-zinc-700/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#DD0000]/30 active:scale-95';

/**
 * Metric card: title + small badge on top, big value with unit,
 * optional progress bar or notice pill, and a footer line with an action.
 */
const StatCard = ({
  title,
  label,
  value,
  unit,
  badge,
  tone = 'black',
  progress,
  notice,
  footer,
  action,
  extra,
  loading = false,
  className = '',
}) => {
  const colors = TONES[tone] || TONES.black;

  if (loading) {
    return (
      <div className={`flex min-h-[200px] flex-col rounded-2xl border border-zinc-200/75 dark:border-white/10 bg-white/75 dark:bg-zinc-900/70 backdrop-blur-md p-5 shadow-sm ${className}`}>
        <div className="flex items-center justify-between">
          <Skeleton className="h-4 w-28" />
          <Skeleton className="h-6 w-16 rounded-lg" />
        </div>
        <Skeleton className="mt-5 h-9 w-24" />
        <Skeleton className="mt-5 h-2 w-full rounded-full" />
        <Skeleton className="mt-auto h-4 w-24" />
      </div>
    );
  }

  const BadgeIcon = badge?.icon;
  const badgeColors = TONES[badge?.tone] || colors;
  const NoticeIcon = notice?.icon;
  const noticeColors = TONES[notice?.tone] || TONES.gold;

  return (
    <div className={`group flex min-h-[200px] flex-col rounded-2xl border border-zinc-200/75 dark:border-white/10 bg-white/75 dark:bg-zinc-900/70 backdrop-blur-md p-5 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.035),0_1px_2px_rgba(0,0,0,0.02)] transition-all duration-300 hover:shadow-[0_12px_28px_-6px_rgba(0,0,0,0.07)] hover:border-zinc-300/80 dark:hover:border-white/20 hover:-translate-y-0.5 ${className}`}>
      <div className="flex items-start justify-between gap-3">
        <h3 className="min-w-0 text-[15px] font-semibold text-zinc-900">{title || label}</h3>
        {badge && (
          <span className="inline-flex shrink-0 items-center gap-1 rounded-lg border border-zinc-200/70 dark:border-white/10 bg-white/80 dark:bg-zinc-800/80 backdrop-blur-xs px-2 py-0.5 text-xs font-medium text-zinc-800 dark:text-zinc-200">
            {BadgeIcon && <BadgeIcon size={13} className={badgeColors.icon} />}
            {badge.text}
          </span>
        )}
      </div>

      <div className="mt-4 flex items-end justify-between gap-2">
        <p className="flex min-w-0 items-baseline gap-1.5">
          <span className="text-4xl font-semibold tracking-tight text-zinc-950 tabular-nums">{value}</span>
          {unit && <span className="truncate text-base text-zinc-500">{unit}</span>}
        </p>
        {extra}
      </div>

      {typeof progress === 'number' && (
        <div
          role="progressbar"
          aria-label={title || label}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(progress)}
          className={`mt-4 h-2 overflow-hidden rounded-full ${colors.track}`}
        >
          <div
            className={`h-full rounded-full ${colors.bar} transition-[width] duration-500`}
            style={{ width: `${Math.max(0, Math.min(100, progress))}%` }}
          />
        </div>
      )}

      {notice && (
        <p className={`mt-4 inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-sm font-medium ${noticeColors.soft}`}>
          {NoticeIcon && <NoticeIcon size={16} />}
          {notice.text}
        </p>
      )}

      {(footer || action) && (
        <div className="mt-auto flex items-center justify-between gap-3 pt-4">
          <span className="min-w-0 truncate text-sm text-zinc-500">{footer}</span>
          {action && (action.to ? (
            <Link to={action.to} className={actionClass}>{action.label}</Link>
          ) : (
            <button type="button" onClick={action.onClick} className={actionClass}>{action.label}</button>
          ))}
        </div>
      )}
    </div>
  );
};

export default StatCard;
